import {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useId,
  useCallback,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

const ARROW_SIZE = 8; // px - w-2 / h-2
const GAP = 2; // px - small gap between arrow and trigger edge
const VIEWPORT_GUTTER = 8; // px - keep this much clear of the viewport edges

interface TooltipProps {
  content: ReactNode;
  children: ReactElement;
  /**
   * Class list for the trigger wrapper span, replacing the default
   * `inline-block`. `inline-block` shrink-wraps to the trigger's intrinsic
   * width, which is correct for most triggers (an icon button, a chip) but
   * wrong for one that is itself meant to fill its container's width - a
   * `w-full` menu-item button inside the wrapper's `inline-block` still
   * shrink-wraps, because `inline-block` has no width of its own for the
   * child's percentage to resolve against. Pass `"block w-full"` for that
   * case so the wrapper takes the parent's width and the trigger's own
   * `w-full` has something real to fill.
   */
  wrapperClassName?: string;
  /**
   * ARIA role for the trigger wrapper span. The wrapper is a plain `<span>`
   * with no role by default, which is invisible to assistive tech in most
   * contexts - but a `role="menu"` container requires its direct children to
   * be `menuitem` / `menuitemradio` / `menuitemcheckbox` / `group` /
   * `separator`, so a caller wrapping a `menuitem` button in a `Tooltip`
   * inside a menu must pass `wrapperRole="none"` to keep the wrapper out of
   * that ownership chain (`ActionMenu`'s disabled-with-reason item is the
   * adopter).
   */
  wrapperRole?: string;
}

function mergeDescribedBy(existing: string | undefined, tooltipId: string | undefined): string | undefined {
  return [existing, tooltipId].filter(Boolean).join(' ') || undefined;
}

export function Tooltip({ content, children, wrapperClassName = 'inline-block', wrapperRole }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  // Whether the panel currently showing was opened by a touch tap, as opposed
  // to hover or focus. Only the touch case needs the panel to accept pointer
  // events - see the panel's className below - so this has to be tracked
  // rather than inferred from `visible` alone.
  const [openedByTouch, setOpenedByTouch] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    /** Viewport-x of the panel's centre after edge-clamping. */
    left: number;
    above: boolean;
    /** Viewport-x of the trigger's centre (where the arrow should point). */
    arrowLeft: number;
  }>({ top: 0, left: 0, above: false, arrowLeft: 0 });

  const wrapperRef = useRef<HTMLSpanElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const tooltipId = useId();

  const updatePosition = useCallback(() => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    // Read panel dimensions from offsetWidth/Height, falling back to 0 on the
    // first call before the panel is laid out. Matches ActionMenu's pattern.
    const panelEl = panelRef.current;
    const panelHeight = panelEl?.offsetHeight ?? 0;
    const panelWidth = panelEl?.offsetWidth ?? 0;
    const halfWidth = panelWidth / 2;

    const triggerCenterX = rect.left + rect.width / 2;
    const viewportWidth = window.innerWidth;

    // Clamp the panel's centre so its left/right edges stay inside the
    // viewport gutter. If the panel is wider than the viewport (rare; only
    // when content is forced wide on a tiny screen) pin it to the left gutter.
    const minCenter = VIEWPORT_GUTTER + halfWidth;
    const maxCenter = viewportWidth - VIEWPORT_GUTTER - halfWidth;
    const clampedCenter =
      minCenter > maxCenter
        ? VIEWPORT_GUTTER + halfWidth
        : Math.min(Math.max(triggerCenterX, minCenter), maxCenter);

    const spaceAbove = rect.top;
    const above = spaceAbove >= panelHeight + ARROW_SIZE + GAP;

    const top = above
      ? rect.top - panelHeight - ARROW_SIZE - GAP
      : rect.bottom + ARROW_SIZE + GAP;

    // Arrow position in panel-local coordinates: where the trigger's centre
    // falls relative to the panel's left edge. When the panel is shifted
    // away from the trigger by viewport-edge clamping this is no longer the
    // panel's centre. Clamp so the arrow stays inside the panel.
    const arrowLocalX = triggerCenterX - (clampedCenter - halfWidth);
    const ARROW_INSET = ARROW_SIZE;
    const arrowMax = Math.max(ARROW_INSET, panelWidth - ARROW_INSET);
    const arrowLeft = Math.min(Math.max(arrowLocalX, ARROW_INSET), arrowMax);

    // eslint-disable-next-line @eslint-react/set-state-in-effect -- layout measurement requires setState
    setPosition({ top, left: clampedCenter, above, arrowLeft });
  }, []);

  // Hides the tooltip and clears the touch-open flag together, so a later
  // hover- or focus-open never inherits pointer-events-auto from a previous
  // touch interaction.
  const hide = useCallback(() => {
    setVisible(false);
    setOpenedByTouch(false);
  }, []);

  // Compute position synchronously before first paint, and track scroll/resize while visible.
  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, { capture: true, passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  // Escape key dismissal while visible. Attached in the capture phase on
  // document so ancestor React handlers calling `e.stopPropagation()` cannot
  // prevent the tooltip from dismissing.
  useEffect(() => {
    if (!visible) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') hide();
    }
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
    };
  }, [visible, hide]);

  // Touch dismissal: a touch device has no hover, so a tap outside the
  // trigger is the only signal that the user is done with the tooltip. This
  // listener is registered only while visible - a list of many tooltip rows
  // must not accumulate one document listener per row. No `capture` needed
  // here (unlike Escape above): dismissal only needs to see a pointerdown
  // that reaches document at all, and capture would fire before a trigger's
  // own pointerdown handler has had a chance to open a *different* tooltip.
  //
  // A tap landing on the panel itself is deliberately left alone here - it
  // dismisses via the panel's own `onClick` below, not via this pointerdown
  // listener. Verified against real Chromium touch dispatch (not assumed):
  // calling `preventDefault()` on the panel's pointerdown does NOT suppress
  // the compatibility `click` the browser generates from the matching
  // touchend, and if the panel is already removed from the DOM by then, that
  // click re-hit-tests and lands on whatever the removal just exposed - a
  // neighbouring row, say - and navigates there. Keeping the panel mounted
  // through to its own `click`, and dismissing only on that click, spends the
  // compatibility click on the (inert) panel instead.
  useEffect(() => {
    if (!visible) return;
    function handlePointerDown(e: PointerEvent) {
      if (wrapperRef.current?.contains(e.target as Node)) return;
      if (panelRef.current?.contains(e.target as Node)) return;
      hide();
    }

    document.addEventListener('pointerdown', handlePointerDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
    };
  }, [visible, hide]);

  // A screen reader announces `aria-describedby` from the element it is focused
  // on, and that is the trigger - the wrapper span carries no tabindex and is
  // never focused. React cannot put an attribute on someone else's element
  // without cloning it, so the attribute is written to the trigger node
  // directly, and the trigger's own `aria-describedby` (a form field describing
  // itself with a hint, say) is preserved alongside and restored on cleanup.
  useEffect(() => {
    const trigger = wrapperRef.current?.firstElementChild;
    if (!(trigger instanceof HTMLElement)) return;

    const own = trigger.getAttribute('aria-describedby');
    const merged = mergeDescribedBy(own ?? undefined, visible ? tooltipId : undefined);
    if (merged) trigger.setAttribute('aria-describedby', merged);
    else trigger.removeAttribute('aria-describedby');

    return () => {
      if (own) trigger.setAttribute('aria-describedby', own);
      else trigger.removeAttribute('aria-describedby');
    };
  }, [visible, tooltipId]);

  return (
    <>
      <span
        ref={wrapperRef}
        role={wrapperRole}
        onMouseEnter={() => setVisible(true)}
        onMouseLeave={() => {
          // Chromium synthesizes a compatibility mouse sequence
          // (mousemove/mousedown/mouseup/click, sometimes with a blur) after
          // every touch tap, generated by walking the DOM from wherever the
          // pointer last stood to the new touch point - real behaviour,
          // reproduced with Chromium's actual touch dispatch, not assumed.
          // The very next tap that is meant to dismiss a touch-opened
          // tooltip (the panel itself, or elsewhere on the row) IS such a
          // sequence: it fires a genuine mouseleave on this wrapper - React
          // delegates it here because the new target sits outside the
          // wrapper's subtree - before that tap's own touchend/click ever
          // lands. Honouring it here would race the tooltip's own touch
          // dismiss paths (the outside-pointerdown listener, the panel's own
          // onClick, Escape) and, observed directly, can remove the panel
          // from the DOM in the middle of the very tap meant to act on it.
          // A real mouse user's hover-exit is the only case this should
          // hide the tooltip for, and there is no such session once touch
          // has opened it - so once touch is in play, only an explicit
          // dismissal path closes the tooltip, never this synthetic leave.
          if (openedByTouch) return;
          hide();
        }}
        onFocus={() => setVisible(true)}
        onBlur={() => {
          // Same synthetic-event hazard as onMouseLeave above, via focus
          // instead of hover: the compatibility mousedown that follows a
          // touch tap can move focus, firing a genuine blur on this wrapper
          // for the same reason. Guard identically.
          if (openedByTouch) return;
          hide();
        }}
        onPointerDown={(e) => {
          // Touch has no hover, so opening on tap is the only way in; mouse
          // and pen already open via mouseenter/focus, so this is additive
          // for touch only. No preventDefault/stopPropagation - a
          // tooltip-wrapped `button` still receives its click normally.
          if (e.pointerType === 'touch') {
            setVisible(true);
            setOpenedByTouch(true);
          }
        }}
        className={wrapperClassName}
      >
        {children}
      </span>
      {visible &&
        createPortal(
          <div
            ref={panelRef}
            id={tooltipId}
            role="tooltip"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              transform: 'translateX(-50%)',
            }}
            // pointer-events-auto only when this open was a touch tap - the
            // panel is portaled outside `wrapperRef`, so on a touch device it
            // floats over neighbouring rows with nothing dismissing it under
            // the user's finger. Without this a tap on the panel - the
            // natural way to close it - fell through to whatever sat
            // underneath (a different table row, say) and navigated there
            // instead. Capturing pointer events here means that tap now
            // lands on the panel, which dismisses it via `onClick` below.
            //
            // A tooltip opened by hover or focus must stay pointer-events-none,
            // even though it is the same panel element. `onFocus` fires
            // wherever the mouse already is, so a tabbed-to trigger can pop
            // the panel directly under the pointer; with pointer-events-auto
            // there, a mousedown on the panel blurs the trigger (unmounting
            // the panel via `onBlur`) before the matching `click` is
            // dispatched, and Chromium then fires no click at all - silently
            // dropping the very click the keyboard-and-mouse user meant to
            // land on whatever was under the panel. Keeping the hover/focus
            // case pointer-events-none lets that click fall straight through
            // to its real target, exactly as it did before touch support was
            // added. Every tooltip's content is static (text or a read-only
            // breakdown), never an interactive control the panel might
            // otherwise shadow.
            //
            // `w-max` (capped by `max-w-xs`) gives the panel an intrinsic
            // width. Without it a `position: fixed` box shrink-to-fits the
            // space between its `left` and the viewport edge, so a panel
            // measured at one `left` and then placed near the right edge
            // re-wrapped narrower than `updatePosition` had measured - the
            // centre clamp and the arrow were then computed for the wider
            // box, leaving the arrow floating outside the bubble.
            className={
              openedByTouch
                ? 'pointer-events-auto z-[9999] w-max max-w-xs rounded-md bg-secondary px-2 py-1 text-xs font-medium text-bg-primary shadow-lg'
                : 'pointer-events-none z-[9999] w-max max-w-xs rounded-md bg-secondary px-2 py-1 text-xs font-medium text-bg-primary shadow-lg'
            }
            // Dismisses a touch tap on the panel. Deliberately on `click`,
            // not `pointerdown`: the outside-pointerdown listener above
            // leaves a tap on the panel alone precisely so the panel stays
            // mounted for its own click to land on - see that listener's
            // comment for why removing it earlier is unsafe. This handler
            // also makes the panel a genuinely "clickable" element, which
            // iOS Safari requires before it will synthesise a compatibility
            // click for a tap at all. Harmless when the panel is
            // pointer-events-none (hover/focus open): no click ever reaches
            // it there, so the handler simply never fires.
            //
            // `stopPropagation()` here is doing real work even though the
            // panel is portaled to `document.body`, outside the trigger's
            // DOM subtree entirely: React dispatches synthetic events along
            // the REACT tree, not the DOM tree, so this click still bubbles
            // to whatever the `Tooltip` is nested under in JSX - a table
            // row with its own `onClick` that navigates, say. That row
            // guards against a click on the trigger itself with a
            // DOM-based `closest('[data-settlement-trigger]')` check, which
            // cannot see this panel at all (it has no DOM ancestor
            // relationship to the trigger), so without stopping it here the
            // dismiss tap on a touch-opened panel also navigated the row
            // underneath - verified against real Chromium touch dispatch,
            // not assumed. Every tooltip's content is inert (static text or
            // a read-only breakdown), so no interactive descendant ever
            // needs this click to keep bubbling.
            onClick={(e) => {
              e.stopPropagation();
              hide();
            }}
          >
            {/* Arrow - rotated square. Its `left` is in panel-local coords,
                tracked separately so it still points at the trigger when the
                panel is clamped away from the trigger's centre near a
                viewport edge. */}
            <span
              aria-hidden="true"
              style={{ left: position.arrowLeft }}
              className={[
                'absolute -translate-x-1/2 w-2 h-2 rotate-45 bg-secondary',
                position.above ? '-bottom-1' : '-top-1',
              ].join(' ')}
            />
            {content}
          </div>,
          document.body,
        )}
    </>
  );
}
